import {
  Anchor,
  Button,
  Checkbox,
  Container,
  Group,
  Paper,
  Select,
  SimpleGrid,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { TurnstileWidget } from '#marketing/components/TurnstileWidget.jsx';
import { getContactSchema } from '#marketing/lib/contact-schema.js';
import {
  CONTACT_CONSENT_WORDING_VERSION,
  CONTACT_INTEREST_OPTIONS,
  CONTACT_ROLE_OPTIONS,
} from '#marketing/lib/marketing-contact-constants.js';
import { localePath, marketingTurnstileLanguage } from '#marketing/lib/marketing-locale.js';
import { marketingRouteMeta } from '#marketing/lib/marketing-route-meta.js';
import { useMarketingLocale } from '#marketing/lib/use-marketing-locale.js';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';

/** @param {{ matches: Array<{ id?: string, pathname?: string, data?: unknown }> }} args */
export function meta({ matches }) {
  return marketingRouteMeta({ matches, page: 'contact', pagePath: '/contact' });
}

/**
 * Submit marketing contact form to Cloudflare Pages Function.
 *
 * @param {object} values
 * @returns {Promise<{ success: boolean }>}
 */
async function submitContactForm(values) {
  const response = await fetch('/api/contact', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...values,
      consentWordingVersion: CONTACT_CONSENT_WORDING_VERSION,
      source: 'pachub.ca/contact',
    }),
  });

  if (!response.ok) {
    return { success: false };
  }

  const data = await response.json();
  return { success: data.success === true };
}

export default function Contact() {
  const { t } = useTranslation('common');
  const locale = useMarketingLocale();
  const [submitting, setSubmitting] = useState(false);

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      name: '',
      email: '',
      role: '',
      schoolName: '',
      cityOrDistrict: '',
      interest: '',
      message: '',
      marketingConsent: false,
      company: '',
      turnstileToken: '',
    },
    validate: schemaResolver(getContactSchema(t), { sync: true }),
  });

  const handleTurnstileSuccess = useCallback(
    (token) => {
      form.setFieldValue('turnstileToken', token);
    },
    [form],
  );

  const roleOptions = CONTACT_ROLE_OPTIONS.map((value) => ({
    value,
    label: t(`marketing.contact.roles.${value}`),
  }));

  const interestOptions = CONTACT_INTEREST_OPTIONS.map((value) => ({
    value,
    label: t(`marketing.contact.interests.${value}`),
  }));

  const handleSubmit = async (values) => {
    if (values.company?.trim()) {
      notifications.show({
        title: t('marketing.contact.successTitle'),
        message: t('marketing.contact.successMessage'),
        color: 'green',
      });
      form.reset();
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitContactForm({
        name: values.name,
        email: values.email,
        role: values.role,
        schoolName: values.schoolName?.trim() || undefined,
        cityOrDistrict: values.cityOrDistrict?.trim() || undefined,
        interest: values.interest,
        message: values.message,
        marketingConsent: values.marketingConsent === true,
        company: values.company,
        turnstileToken: values.turnstileToken,
      });

      if (result.success) {
        notifications.show({
          title: t('marketing.contact.successTitle'),
          message: t('marketing.contact.successMessage'),
          color: 'green',
        });
        form.reset();
      } else {
        notifications.show({
          title: t('marketing.contact.errorMessage'),
          color: 'red',
        });
      }
    } catch {
      notifications.show({
        title: t('marketing.contact.errorMessage'),
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container size="sm" py="xl">
      <Paper withBorder shadow="sm" radius="md" p="xl" bg="white">
        <Title order={2} ta="center" mb="sm">
          {t('marketing.contact.title')}
        </Title>
        <Text c="dimmed" ta="center" mb="lg">
          {t('marketing.contact.intro')}
        </Text>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label={t('marketing.contact.nameLabel')}
              placeholder={t('marketing.contact.namePlaceholder')}
              name="name"
              required
              {...form.getInputProps('name')}
            />
            <TextInput
              label={t('marketing.contact.emailLabel')}
              placeholder={t('marketing.contact.emailPlaceholder')}
              name="email"
              required
              {...form.getInputProps('email')}
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }} mt="md">
            <Select
              label={t('marketing.contact.roleLabel')}
              placeholder={t('marketing.contact.rolePlaceholder')}
              data={roleOptions}
              required
              {...form.getInputProps('role')}
            />
            <Select
              label={t('marketing.contact.interestLabel')}
              placeholder={t('marketing.contact.interestPlaceholder')}
              data={interestOptions}
              required
              {...form.getInputProps('interest')}
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }} mt="md">
            <TextInput
              label={t('marketing.contact.schoolLabel')}
              placeholder={t('marketing.contact.schoolPlaceholder')}
              name="schoolName"
              {...form.getInputProps('schoolName')}
            />
            <TextInput
              label={t('marketing.contact.cityLabel')}
              placeholder={t('marketing.contact.cityPlaceholder')}
              name="cityOrDistrict"
              {...form.getInputProps('cityOrDistrict')}
            />
          </SimpleGrid>

          <Textarea
            mt="md"
            label={t('marketing.contact.messageLabel')}
            placeholder={t('marketing.contact.messagePlaceholder')}
            maxRows={10}
            minRows={5}
            autosize
            name="message"
            required
            {...form.getInputProps('message')}
          />

          <Checkbox
            mt="md"
            label={t('marketing.contact.marketingConsentLabel')}
            {...form.getInputProps('marketingConsent', { type: 'checkbox' })}
          />

          <Text size="sm" c="dimmed" mt="md">
            {t('marketing.contact.privacyDisclosureBefore')}{' '}
            <Anchor component={Link} to={localePath(locale, '/privacy')} size="sm">
              {t('marketing.contact.privacyPolicyLink')}
            </Anchor>
            .
          </Text>

          <input
            type="text"
            name="company"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
            {...form.getInputProps('company')}
          />

          <Group justify="center" mt="md">
            <TurnstileWidget
              siteKey={TURNSTILE_SITE_KEY}
              language={marketingTurnstileLanguage(locale)}
              onSuccess={handleTurnstileSuccess}
            />
          </Group>
          {form.errors.turnstileToken && (
            <Text c="red" size="sm" ta="center" mt="xs">
              {form.errors.turnstileToken}
            </Text>
          )}

          <Group justify="center" mt="xl">
            <Button type="submit" size="md" loading={submitting}>
              {t('marketing.contact.submitButton')}
            </Button>
          </Group>
        </form>
      </Paper>
    </Container>
  );
}
